import React from 'react';
import { Link } from 'react-router-dom';
import { useSectionRoute } from '../../hooks/useSectionRoute';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { academicActivity, accommodationPressure, careerOutcomes, engagementEvents, feedbackByCategory, institutionSummary, populationByFaculty, recurringIssues, safetySnapshot } from '../../data/institution';
import { institutionServiceLoad } from '../../data/services';
import { naira, percent } from '../../utils/format';
import { AttentionBoard } from '../../components/operator/AttentionBoard';
import { DataTable } from '../../components/ui/DataTable';
import { BadgeCheckIcon } from 'lucide-react';
const TABS = ['Today', 'Overview', 'Students', 'Services', 'Feedback',
// Named for what it is — aggregate trends — so it cannot be mistaken for
// the operational safety desk at /institution/safety, which receives live
// alerts. The two are different jobs and now have different addresses.
'Safety trends', 'Accommodation', 'Career'] as const;
export function InstitutionOverview() {
  const [tab, setTab] = useSectionRoute('/institution', TABS);

  // Derived rather than asserted: the worst feedback category, the tightest
  // housing area and the issues students keep raising.
  const worstFeedback = [...feedbackByCategory].sort((a, b) => b.open - a.open)[0];
  const tightest = [...accommodationPressure].sort((a, b) => a.listedBeds - b.listedBeds)[0];
  const rising = recurringIssues.filter(issue => issue.trend.includes('+') || issue.trend.toLowerCase().includes('up'));
  const leastEngaged = [...populationByFaculty].sort((a, b) => a.engaged / a.students - b.engaged / b.students)[0];
  return <div className="space-y-6" data-id="element-8606" data-mp="23y6ppklzjm0l">
      <PageHeader eyebrow={institutionSummary.name} title="Institution console" description={`${institutionSummary.campus} · ${institutionSummary.integrationLevel}. You only see data your institution is authorized to access, and never another institution's.`} data-id="element-8607" data-mp="241kqwpiwxp6g" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Institution sections" data-id="element-8608" data-mp="1o8qqwck0bk1h" />

      {tab === 'Today' && <AttentionBoard emptyNote="Nothing in the aggregate figures is moving in a direction that needs a response this week." items={[...(worstFeedback ? [{
      id: 'feedback',
      title: `${worstFeedback.category} feedback is not being closed`,
      detail: `${worstFeedback.open} items are open and the median time to resolution is ${worstFeedback.medianDays} days. Students see the delay, not the reason for it.`,
      urgency: 'now' as const,
      action: {
        label: 'Open feedback',
        to: '/institution/feedback'
      }
    }] : []), ...rising.map(issue => ({
      id: issue.issue,
      title: issue.issue,
      detail: `Raised ${issue.reports} times and trending ${issue.trend}. A recurring report is a signal about a process, not about the students raising it.`,
      urgency: 'soon' as const
    })), ...(tightest ? [{
      id: 'housing',
      title: `Accommodation is tightest in ${tightest.neighbourhood}`,
      detail: `${tightest.listedBeds} beds listed against ${tightest.demand} demand, median ${tightest.medianRent}. Campuzon lists what landlords publish; it does not create supply.`,
      urgency: 'soon' as const
    }] : []), ...(leastEngaged ? [{
      id: 'engagement',
      title: `${leastEngaged.faculty} engages least`,
      detail: `${leastEngaged.engaged.toLocaleString()} of ${leastEngaged.students.toLocaleString()} students are active. Low engagement may mean the faculty publishes little here, not that its students are disengaged.`,
      urgency: 'watch' as const
    }] : [])]} summary={[{
      label: 'Active this week',
      value: institutionSummary.activeThisWeek.toLocaleString(),
      note: `of ${institutionSummary.students.toLocaleString()} students.`
    }, {
      label: 'Integration level',
      value: institutionSummary.integrationLevel,
      note: 'Decides what your students can prove about their enrolment.'
    }, {
      label: 'Verification queue',
      value: 'Open',
      note: 'Affiliation requests waiting on your registry.'
    }]} data-id="element-8609" data-mp="1j8kzmxhnfzjx" />}

      {tab === 'Services' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-8610" data-mp="3bbo2wlfxsspz">
          <Panel className="lg:col-span-2" title="Academic service queues" description="Requests students have raised through Campuzon for services this institution provides." bodyClassName="p-0" data-id="element-8611" data-mp="3ga7ouluj64ic">
            <ul className="divide-y divide-line" data-id="element-8612" data-mp="3pz976iojsxzh">
              {institutionServiceLoad.queues.map(queue => <li key={queue.label} className="px-5 py-3.5" data-id="element-8613" data-mp="3v0yf3pgh4vjc">
                  <p className="flex flex-wrap items-baseline gap-2" data-id="element-8614" data-mp="2wcq1oalkxqv">
                    <span className="text-[13px] font-medium text-ink" data-id="element-8615" data-mp="7uo0o18pvca5">{queue.label}</span>
                    {queue.overdue > 0 && <Badge tone="warn" data-id="element-8616" data-mp="24n7qitfr1l3x">{queue.overdue} past target</Badge>}
                    <span className="cz-num ml-auto text-sm font-semibold text-ink" data-id="element-8617" data-mp="29l95p3a210a4">{queue.open} open</span>
                  </p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-8618" data-mp="3cxg97b01hg4l">
                    Median {queue.medianDays} days to completion
                  </p>
                </li>)}
            </ul>
          </Panel>

          <div className="space-y-6" data-id="element-8619" data-mp="3lyefjzxy9zqc">
            <Panel title="What you offer through Campuzon" data-id="element-8620" data-mp="3gy9vjzxu2mvd">
              <ul className="space-y-1.5" data-id="element-8621" data-mp="3gwp4ikaq9m29">
                {institutionServiceLoad.officiallyProvided.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-8622" data-mp="3qw7jembs6hng">
                    <BadgeCheckIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-8623" data-mp="8i6zkrg32mmv" /> {item}
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-8624" data-mp="4hwiaimuc9sp">
                Not offered here
              </p>
              <ul className="mt-1 space-y-1" data-id="element-8625" data-mp="dhb5n97po48z">
                {institutionServiceLoad.notOfferedThroughCampuzon.map(item => <li key={item} className="text-[13px] leading-6 text-ink-muted" data-id="element-8626" data-mp="x44or9y63s9t">
                    · {item}
                  </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-8627" data-mp="n55oaenk32xw">
                You control which services appear as officially provided. Anything you do not list is
                shown to students as an independent or self-managed route.
              </p>
            </Panel>

            <Panel title="Verified providers operating here" data-id="element-8628" data-mp="j3bpyd0u2b0w">
              <ul className="divide-y divide-line" data-id="element-8629" data-mp="j4v705ubcq3q">
                {institutionServiceLoad.providers.map(provider => <li key={provider.name} className="flex flex-wrap items-center gap-2 py-2 first:pt-0" data-id="element-8630" data-mp="4cky54z8rbxs">
                    <span className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-8631" data-mp="3rj3u4ye2gooi">{provider.name}</span>
                    <Badge tone="ok" data-id="element-8632" data-mp="3rlhp778qy0w2">{provider.status}</Badge>
                    <span className="cz-num text-[12px] text-ink-subtle" data-id="element-8633" data-mp="3vk79ljs7cip7">{provider.requests}</span>
                  </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-8634" data-mp="aw0ngw4jfep7">
                These are independent providers verified by Campuzon. None of them acts for this
                institution, and none can be presented as doing so.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Overview' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-8635" data-mp="3t2x0dzgusgz7">
          <Panel className="lg:col-span-2" title="Academic activity this semester" bodyClassName="p-0" data-id="element-8636" data-mp="3oax4qj7tga8f">
            <ul className="divide-y divide-line" data-id="element-8637" data-mp="3jb14mv28p2y5">
              {academicActivity.map(item => <li key={item.label} className="flex items-baseline justify-between gap-4 px-5 py-3.5" data-id="element-8638" data-mp="3eczig19ionig">
                  <span className="text-[13px] text-ink-muted" data-id="element-8639" data-mp="39dmluosug22o">{item.label}</span>
                  <span className="flex items-baseline gap-3" data-id="element-8640" data-mp="35c1flmatem1g">
                    <span className="cz-num text-sm font-semibold text-ink" data-id="element-8641" data-mp="35dw0x1q00g62">{item.value}</span>
                    <span className="cz-num w-28 text-right text-[12px] text-ink-subtle" data-id="element-8642" data-mp="1kth6u786squl">
                      {item.delta}
                    </span>
                  </span>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Population" data-id="element-8643" data-mp="1yu1ov16a2in1">
            <p className="cz-num text-3xl font-semibold tracking-tight text-ink" data-id="element-8644" data-mp="1yszzdroiltls">
              {institutionSummary.students.toLocaleString()}
            </p>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-8645" data-mp="164b78i5fsym6">Students with a Campuzon account</p>
            <div className="mt-4" data-id="element-8646" data-mp="1b47b8he4wq1q">
              <Meter value={percent(institutionSummary.activeThisWeek, institutionSummary.students)} label="Active this week" trailing={`${percent(institutionSummary.activeThisWeek, institutionSummary.students)}%`} data-id="element-8647" data-mp="1b35pnizhsl5r" />
            </div>
          </Panel>
          <Panel className="lg:col-span-3" title="Engagement — events" bodyClassName="p-0" data-id="element-8648" data-mp="ncs2o6vrlci1">
            <ul className="divide-y divide-line" data-id="element-8649" data-mp="15r0377gwf1vg">
              {engagementEvents.map(event => <li key={event.title} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-id="element-8650" data-mp="1ap1nfvq2978i">
                  <div className="min-w-0 flex-1" data-id="element-8651" data-mp="1fq8svo4qduc0">
                    <p className="text-[13px] font-medium text-ink" data-id="element-8652" data-mp="1koiq04a0n509">{event.title}</p>
                    <p className="text-[12px] text-ink-muted" data-id="element-8653" data-mp="n11y908edow1">{event.organizer}</p>
                  </div>
                  <div className="w-48" data-id="element-8654" data-mp="rzvc4trljhwq">
                    <Meter value={percent(event.registrations, event.capacity)} trailing={`${event.registrations}/${event.capacity}`} data-id="element-8655" data-mp="s0wnkzciy6ez" />
                  </div>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Students' && <Panel title="Students by faculty" bodyClassName="p-0" data-id="element-8656" data-mp="99ndooyudfmv">
          <ul className="divide-y divide-line" data-id="element-8657" data-mp="982jj0gfvypj">
            {populationByFaculty.map(row => <li key={row.faculty} className="flex flex-wrap items-center gap-4 px-5 py-3.5" data-id="element-8658" data-mp="2mjdfghoh7c9x">
                <p className="w-40 shrink-0 text-[13px] font-medium text-ink" data-id="element-8659" data-mp="2iithxczzq6n9">{row.faculty}</p>
                <div className="min-w-[180px] flex-1" data-id="element-8660" data-mp="2dhxhj9eul2ml">
                  <Meter value={percent(row.engaged, row.students)} label="Engaged on Campuzon" trailing={`${percent(row.engaged, row.students)}%`} data-id="element-8661" data-mp="28kdylw77g1ob" />
                </div>
                <p className="cz-num w-32 text-right text-[13px] text-ink-muted" data-id="element-8662" data-mp="1ysrjhorqxiga">
                  {row.students.toLocaleString()} students
                </p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Feedback' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-8663" data-mp="1kut4ch5lphzq">
          <Panel title="Feedback by category" bodyClassName="p-0" data-id="element-8664" data-mp="1kwnp9vgvha3t">
            <ul className="divide-y divide-line" data-id="element-8665" data-mp="1fwrmfzjwvopu">
              {feedbackByCategory.map(row => <li key={row.category} className="flex items-center gap-4 px-5 py-3.5" data-id="element-8666" data-mp="1672xaktjl0gd">
                  <p className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-8667" data-mp="1b7yvchr7psdv">{row.category}</p>
                  <span className="cz-num text-[13px] text-warn-700" data-id="element-8668" data-mp="x87nyzv7ba8u">{row.open} open</span>
                  <span className="cz-num text-[13px] text-ink-muted" data-id="element-8669" data-mp="117zrgy5qmq7c">{row.resolved} resolved</span>
                  <span className="cz-num w-20 text-right text-[13px] text-ink-muted" data-id="element-8670" data-mp="1oy2gf5ecvg1q">
                    {row.medianDays}d median
                  </span>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Recurring issues" bodyClassName="p-0" data-id="element-8671" data-mp="1g5ikwdtd1rl4">
            <ul className="divide-y divide-line" data-id="element-8672" data-mp="1k6u4a1ln5gkv">
              {recurringIssues.map(issue => <li key={issue.issue} className="flex items-center gap-3 px-5 py-3.5" data-id="element-8673" data-mp="27wy5y3f9r698">
                  <p className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-8674" data-mp="2cog98y4rlixd">{issue.issue}</p>
                  <span className="cz-num text-[13px] font-medium text-ink" data-id="element-8675" data-mp="18xrk9uyqdoc">{issue.reports}</span>
                  <Badge tone={issue.trend === 'Rising' ? 'warn' : issue.trend === 'Falling' ? 'ok' : 'neutral'} data-id="element-8676" data-mp="3sf8sm5zz3efo">
                    {issue.trend}
                  </Badge>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Safety trends' && <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8677" data-mp="3ef7pejw8jux1">
          {safetySnapshot.map(item => <Panel key={item.label} data-id="element-8678" data-mp="39gwdm3mdrac6">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-8679" data-mp="39hxqmdmyi6wj">{item.value}</p>
              <p className="mt-1 text-[13px] leading-5 text-ink-muted" data-id="element-8680" data-mp="3of8cmgx86v6i">{item.label}</p>
            </Panel>)}
          <div className="sm:col-span-2 lg:col-span-4" data-id="element-8681" data-mp="3jgna2aui3zew">
            <Panel title="Handling standards" data-id="element-8682" data-mp="13md8oonw87i3">
              <p className="text-body-sm leading-6 text-ink-muted" data-id="element-8683" data-mp="13piv1w550va4">
                These are aggregate trends, not live cases. Anonymous reports never carry identifying
                data into this console. Confidential reports are opened only by named safeguarding
                staff, and every access is written to the audit log.
              </p>
              <p className="mt-3 text-body-sm text-ink-muted" data-id="element-8684" data-mp="3dc8umv7qilv4">
                Alerts and individual reports are answered on the{' '}
                <Link to="/institution/safety" className="font-medium text-brand-700 underline" data-id="element-8685" data-mp="3iaaehiczip7n">
                  campus safety desk
                </Link>
                .
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Accommodation' && <Panel title="Accommodation pressure by neighbourhood" description="Aggregated and anonymized. No individual student's housing activity is exposed here." bodyClassName="p-0" data-id="element-8686" data-mp="wezovqb0z70">
          <DataTable caption="Accommodation pressure by neighbourhood" rowKey={row => row.neighbourhood} rows={accommodationPressure} columns={[{
        header: 'Neighbourhood',
        primary: true,
        cell: row => row.neighbourhood
      }, {
        header: 'Students seeking',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-8688" data-mp="f38loemg0g84">{row.demand.toLocaleString()}</span>
      }, {
        header: 'Beds listed',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-8689" data-mp="17rxelqdchf8s">{row.listedBeds.toLocaleString()}</span>
      }, {
        header: 'Median rent',
        align: 'right',
        cell: row => <span className="cz-num" data-id="element-8690" data-mp="xxx0x03zz1og">{naira(row.medianRent)}</span>
      }, {
        header: 'Pressure',
        status: true,
        cell: row => {
          const ratio = Math.round(row.demand / row.listedBeds * 10) / 10;
          return <Badge tone={ratio >= 3 ? 'danger' : ratio >= 2 ? 'warn' : 'ok'} data-id="element-8691" data-mp="3fxzlrjtqt6rl">
                      {ratio} students per bed
                    </Badge>;
        }
      }]} data-id="element-8687" data-mp="9uqp4uffpqxv" />
        </Panel>}

      {tab === 'Career' && <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8692" data-mp="3b6zpmgsg6sq5">
          {careerOutcomes.map(item => <Panel key={item.label} data-id="element-8693" data-mp="2x7ht1f8wub68">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-8694" data-mp="2x6f6tkmj8wwn">{item.value}</p>
              <p className="mt-1 text-[13px] leading-5 text-ink-muted" data-id="element-8695" data-mp="3272srrvwmnk0">{item.label}</p>
            </Panel>)}
        </div>}
    </div>;
}